import type { Dispatch, SetStateAction } from 'react';
import { memo, useEffect, useState } from 'react';

import { EngravingOptionType, type OptionType, type StyleType } from '../types';
import { EngravingDateInput } from './EngravingDateInput';
import { EngravingLettersInput } from './EngravingLettersInput';
import { EngravingMessageInput } from './EngravingMessageInput';

export interface EngravingInputsProps {
  engravingValue: string;
  hasError: boolean;
  isWithSeparators: boolean;
  resetErrors: () => void;
  setEngravingValue: Dispatch<SetStateAction<string>>;
  setIsWithSeparators: Dispatch<SetStateAction<boolean>>;
  style: StyleType;
  type: OptionType;
}

const EngravingInputsComponent = ({
  engravingValue,
  hasError,
  isWithSeparators,
  resetErrors,
  setEngravingValue,
  setIsWithSeparators,
  style,
  type,
}: EngravingInputsProps) => {
  const [letters, setLetters] = useState<string[]>(['', '', '']);
  const [dates, setDates] = useState<string[]>([
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
  ]);

  const sanitizeValue = (): string => {
    if (type === EngravingOptionType.Initials) {
      if (isWithSeparators) {
        const nonEmpty = letters.filter((l) => l !== '');
        return nonEmpty.join('.');
      }
      return letters.join('');
    } else {
      // Insert a '.' after the 2nd and 4th elements
      // E.g., 12122025 => 12.12.2025
      const parts = [
        dates.slice(0, 2).join(''),
        dates.slice(2, 4).join(''),
        dates.slice(4, 8).join(''),
      ];
      return parts.filter(Boolean).join('.');
    }
  };

  useEffect(() => {
    if (type === EngravingOptionType.Message) return;
    const value = sanitizeValue();
    setEngravingValue(value);
    resetErrors();
  }, [letters, dates, type, isWithSeparators]);

  let renderedInput = (
    <EngravingDateInput dates={dates} setDates={setDates} style={style} />
  );

  if (type === EngravingOptionType.Message) {
    renderedInput = (
      <EngravingMessageInput
        hasError={hasError}
        message={engravingValue}
        setMessage={setEngravingValue}
        style={style}
      />
    );
  } else if (type === EngravingOptionType.Initials) {
    renderedInput = (
      <EngravingLettersInput
        isWithSeparators={isWithSeparators}
        letters={letters}
        setIsWithSeparators={setIsWithSeparators}
        setLetters={setLetters}
        style={style}
      />
    );
  }

  return <>{renderedInput}</>;
};

export const EngravingInputs = memo(EngravingInputsComponent);
